import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
  RefreshControl,
  ScrollView,
} from 'react-native';
import { Card, Chip, Button, Divider } from 'react-native-paper';
import { useTheme } from '../contexts/ThemeContext';
import apiService, { CulturalContent, CulturalContentStats } from '../services/ApiService';
import { ThemedContainer } from '../components/ThemedContainer';

interface CultureScreenProps {
  navigation: any;
}

const CultureScreen: React.FC<CultureScreenProps> = ({ navigation }) => {
  const { themeData } = useTheme();
  const [culturalContent, setCulturalContent] = useState<CulturalContent[]>([]);
  const [stats, setStats] = useState<CulturalContentStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedContentType, setSelectedContentType] = useState('');

  const loadCulturalContent = async (page = 1, refresh = false) => {
    try {
      setLoading(true);
      
      const filters: any = {
        page,
        limit: 20,
      };

      if (searchQuery) {
        filters.search = searchQuery;
      }

      if (selectedCategory) {
        filters.category = selectedCategory;
      }

      if (selectedContentType) {
        filters.contentType = selectedContentType;
      }

      const response = await apiService.getCulturalContent(filters);
      
      if (refresh || page === 1) {
        setCulturalContent(response.data);
      } else {
        setCulturalContent(prev => [...prev, ...response.data]);
      }
      
      setHasMore(response.pagination.hasNext);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error loading cultural content:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const loadStats = async () => {
    try {
      const statsData = await apiService.getCulturalContentStats();
      setStats(statsData);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  useEffect(() => {
    loadCulturalContent(1, true);
    loadStats();
  }, []);

  useEffect(() => {
    loadCulturalContent(1, true);
  }, [searchQuery, selectedCategory, selectedContentType]);

  const onRefresh = () => {
    setRefreshing(true);
    loadCulturalContent(1, true);
    loadStats();
  };

  const loadMore = () => {
    if (hasMore && !loading) {
      loadCulturalContent(currentPage + 1);
    }
  };

  const renderContentItem = ({ item }: { item: CulturalContent }) => (
    <TouchableOpacity
      onPress={() => {
        navigation.navigate('CulturalContentDetail', { content: item });
      }}
    >
      <Card
        style={[
          styles.card,
          {
            backgroundColor: themeData.colors.cardBackground,
            borderColor: themeData.colors.cardBorder,
          }
        ]}
      >
      <Card.Content>
        <View style={styles.cardHeader}>
          <Text
            style={[
              styles.title,
              { color: themeData.colors.text }
            ]}
            numberOfLines={2}
          >
            {item.title}
          </Text>
          <View style={styles.typeChip}>
            <Chip
              mode="outlined"
              textStyle={{ color: themeData.colors.highlight1 }}
              style={{ borderColor: themeData.colors.highlight1 }}
            >
              {item.content_type}
            </Chip>
          </View>
        </View>

        <Text
          style={[
            styles.summary,
            { color: themeData.colors.textSecondary }
          ]}
          numberOfLines={3}
        >
          {item.summary}
        </Text>

        {item.tags && item.tags.length > 0 && (
          <View style={styles.tagsContainer}>
            {item.tags.slice(0, 3).map((tag, index) => (
              <Chip
                key={index}
                mode="outlined"
                compact
                textStyle={{ color: themeData.colors.highlight2 }}
                style={[
                  styles.tagChip,
                  { borderColor: themeData.colors.highlight2 }
                ]}
              >
                {tag}
              </Chip>
            ))}
            {item.tags.length > 3 && (
              <Text style={[styles.moreTags, { color: themeData.colors.textTertiary }]}>
                +{item.tags.length - 3} more
              </Text>
            )}
          </View>
        )}

        <View style={styles.cardFooter}>
          <Text style={[styles.date, { color: themeData.colors.textTertiary }]}>
            {new Date(item.created_at).toLocaleDateString()}
          </Text>
          {item.author && (
            <Text style={[styles.author, { color: themeData.colors.textTertiary }]}>
              by {item.author}
            </Text>
          )}
        </View>
      </Card.Content>
    </Card>
    </TouchableOpacity>
  );

  const renderStatsCard = () => {
    if (!stats || !stats.byContentType || !stats.topTags) {
      return (
        <Card
          style={[
            styles.statsCard,
            {
              backgroundColor: themeData.colors.cardBackground,
              borderColor: themeData.colors.cardBorder,
            }
          ]}
        >
          <Card.Content>
            <Text style={[styles.statsTitle, { color: themeData.colors.text }]}>
              Cultural Content Overview
            </Text>
            <ActivityIndicator size="small" color={themeData.colors.highlight1} />
          </Card.Content>
        </Card>
      );
    }

    return (
      <Card
        style={[
          styles.statsCard,
          {
            backgroundColor: themeData.colors.cardBackground,
            borderColor: themeData.colors.cardBorder,
          }
        ]}
      >
        <Card.Content>
          <Text style={[styles.statsTitle, { color: themeData.colors.text }]}>
            Cultural Content Overview
          </Text>
          
          <View style={styles.statsGrid}>
            <View style={styles.statItem}>
              <Text style={[styles.statNumber, { color: themeData.colors.highlight1 }]}>
                {stats.total || 0}
              </Text>
              <Text style={[styles.statLabel, { color: themeData.colors.textSecondary }]}>
                Total Items
              </Text>
            </View>
            
            <View style={styles.statItem}>
              <Text style={[styles.statNumber, { color: themeData.colors.highlight2 }]}>
                {stats.byContentType?.length || 0}
              </Text>
              <Text style={[styles.statLabel, { color: themeData.colors.textSecondary }]}>
                Content Types
              </Text>
            </View>
            
            <View style={styles.statItem}>
              <Text style={[styles.statNumber, { color: themeData.colors.highlight3 }]}>
                {stats.topTags?.length || 0}
              </Text>
              <Text style={[styles.statLabel, { color: themeData.colors.textSecondary }]}>
                Top Tags
              </Text>
            </View>
          </View>
        </Card.Content>
      </Card>
    );
  };

  const renderFilters = () => (
    <View style={styles.filtersContainer}>
      <TextInput
        style={[
          styles.searchInput,
          {
            backgroundColor: themeData.colors.surfaceVariant,
            color: themeData.colors.text,
            borderColor: themeData.colors.cardBorder,
          }
        ]}
        placeholder="Search cultural content..."
        placeholderTextColor={themeData.colors.textTertiary}
        value={searchQuery}
        onChangeText={setSearchQuery}
      />
      
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.filterChips}>
        <Chip
          mode={selectedContentType === '' ? 'flat' : 'outlined'}
          selected={selectedContentType === ''}
          onPress={() => setSelectedContentType('')}
          style={styles.filterChip}
          textStyle={{ color: themeData.colors.text }}
        >
          All Types
        </Chip>
        {stats?.byContentType?.map((type) => (
          <Chip
            key={type.content_type}
            mode={selectedContentType === type.content_type ? 'flat' : 'outlined'}
            selected={selectedContentType === type.content_type}
            onPress={() => setSelectedContentType(type.content_type)}
            style={styles.filterChip}
            textStyle={{ color: themeData.colors.text }}
          >
            {type.content_type} ({type.count})
          </Chip>
        ))}
      </ScrollView>
    </View>
  );

  return (
    <View style={[styles.mainContainer, { backgroundColor: themeData.colors.background }]}>
      <View style={styles.container}>
        <Text style={[styles.header, { color: themeData.colors.text }]}>
          🍹 Tiki Culture
        </Text>
        <Text style={[styles.subtitle, { color: themeData.colors.textSecondary }]}>
          Discover Polynesian pop culture, history, and traditions
        </Text>

        {renderStatsCard()}
        {renderFilters()}

        <FlatList
          data={culturalContent}
          renderItem={renderContentItem}
          keyExtractor={(item) => item.id.toString()}
          style={styles.flatList}
          contentContainerStyle={styles.listContainer}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
          }
          onEndReached={loadMore}
          onEndReachedThreshold={0.1}
          showsVerticalScrollIndicator={true}
          ListFooterComponent={
            loading && hasMore ? (
              <ActivityIndicator size="large" color={themeData.colors.highlight1} />
            ) : null
          }
          ListEmptyComponent={
            !loading ? (
              <View style={styles.emptyContainer}>
                <Text style={[styles.emptyText, { color: themeData.colors.textSecondary }]}>
                  No cultural content found
                </Text>
              </View>
            ) : null
          }
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  mainContainer: {
    flex: 1,
  },
  container: {
    flex: 1,
    padding: 16,
  },
  header: {
    fontSize: 28,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 16,
    marginBottom: 20,
  },
  statsCard: {
    marginBottom: 20,
    borderWidth: 1,
  },
  statsTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 16,
  },
  statsGrid: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  statItem: {
    alignItems: 'center',
  },
  statNumber: {
    fontSize: 24,
    fontWeight: 'bold',
  },
  statLabel: {
    fontSize: 12,
    marginTop: 4,
  },
  filtersContainer: {
    marginBottom: 20,
  },
  searchInput: {
    height: 48,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 16,
    marginBottom: 12,
    fontSize: 16,
  },
  filterChips: {
    flexDirection: 'row',
  },
  filterChip: {
    marginRight: 8,
  },
  listContainer: {
    paddingBottom: 20,
  },
  flatList: {
    flex: 1,
  },
  card: {
    marginBottom: 16,
    borderWidth: 1,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  title: {
    fontSize: 18,
    fontWeight: 'bold',
    flex: 1,
    marginRight: 8,
  },
  typeChip: {
    alignSelf: 'flex-start',
  },
  summary: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 12,
  },
  tagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    marginBottom: 12,
  },
  tagChip: {
    marginRight: 8,
    marginBottom: 4,
  },
  moreTags: {
    fontSize: 12,
    fontStyle: 'italic',
  },
  cardFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  date: {
    fontSize: 12,
  },
  author: {
    fontSize: 12,
    fontStyle: 'italic',
  },
  emptyContainer: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  emptyText: {
    fontSize: 16,
  },
});

export default CultureScreen; 